import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient, useQueries } from '@tanstack/react-query';
import Button from '../../components/ui/Button';
import { useRowSelection } from '../../components/requests/BulkSelection';
import { selectColumn } from '../../components/requests/RequestActions';
import { usePermission } from '../../auth/usePermission';
import PageHeader from '../../components/layout/PageHeader';
import Table from '../../components/ui/Table';
import Input from '../../components/ui/Input';
import DatePicker from '../../components/ui/DatePicker';
import Badge, { StatusBadge } from '../../components/ui/Badge';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import ProfilePhoto from '../../components/candidates/ProfilePhoto';
import CategoryBadge from '../../components/candidates/CategoryBadge';
import StepProgress from '../../components/candidates/StepProgress';
import WithdrawDialog from '../../components/candidates/WithdrawDialog';
import ConvertVehicleTypeModal, { targetVehicleCategory } from '../../components/candidates/ConvertVehicleTypeModal';
import IconButton from '../../components/ui/IconButton';
import { IconEye, IconTrash, IconWithdraw, IconSwap } from '../../components/ui/icons';
import Can from '../../auth/Can';
import { listCandidates, deleteCandidate, withdrawCandidate } from '../../api/candidates';
import CandidateFilterBar, { useCandidateFilters } from '../../components/filters/CandidateFilterBar';
import { listWorkflowCategories } from '../../api/workflow';
import { pushToast } from '../../components/ui/toastBus';
import SearchSummary, { withMatchColumn } from '../../components/ui/SearchMatch';

const STATUS_TABS = [
  { value: 'registered,in_progress', label: 'Active' },
  { value: 'rejected,withdrawn,hold', label: 'Inactive' },
  { value: 'hired', label: 'Hired' },
];

export default function CandidatesList() {
  const queryClient = useQueryClient();
  const { can } = usePermission();

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState(STATUS_TABS[0].value);
  const filters = useCandidateFilters();
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  const [page, setPage] = useState(1);
  const [withdrawTarget, setWithdrawTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [convertTarget, setConvertTarget] = useState(null);
  const [bulkAction, setBulkAction] = useState(null); // 'withdraw' | 'delete'
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const categoriesQuery = useQuery({ queryKey: ['workflow-categories'], queryFn: listWorkflowCategories });
  // A changed filter starts back at page 1.
  useEffect(() => setPage(1), [filters.params]);



  const params = {
    search: search || undefined,
    status: activeTab,
    ...filters.params,
    created_from: createdFrom || undefined,
    created_to: createdTo || undefined,
    page,
  };

  const candidatesQuery = useQuery({ queryKey: ['candidates', params], queryFn: () => listCandidates(params) });

  const countableTabs = STATUS_TABS.filter((t) => t.value);
  const countQueries = useQueries({
    queries: countableTabs.map((t) => ({
      queryKey: ['candidates-count', t.value, filters.params],
      queryFn: () => listCandidates({ ...filters.params, status: t.value, per_page: 1 }),
      select: (data) => data.total,
    })),
  });

  const withdrawMutation = useMutation({
    mutationFn: ({ id, reason }) => withdrawCandidate(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
      pushToast({ type: 'success', message: 'Candidate marked as backout.' });
      setWithdrawTarget(null);
    },
    onError: (err) => {
      pushToast({ type: 'error', message: err.response?.data?.message || 'Could not mark candidate as backout.' });
      setWithdrawTarget(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteCandidate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
      pushToast({ type: 'success', message: 'Candidate deleted.' });
      setDeleteTarget(null);
    },
    onError: (err) => {
      pushToast({ type: 'error', message: err.response?.data?.message || 'Could not delete candidate.' });
      setDeleteTarget(null);
    },
  });

  const rows = candidatesQuery.data?.data || [];
  const canWithdrawRow = (row) => can('candidates.withdraw') && ['registered', 'in_progress'].includes(row.status);
  const canDeleteRow = () => can('candidates.delete');
  const isSelectable = (row) => canWithdrawRow(row) || canDeleteRow(row);
  const selection = useRowSelection(rows, (r) => r.id, isSelectable);
  const selectedRows = rows.filter((r) => selection.selectedIds.includes(r.id));
  const withdrawable = selectedRows.filter(canWithdrawRow);
  const deletable = selectedRows.filter(canDeleteRow);

  // Runs the existing single-candidate call one at a time so a failure on one doesn't stop the rest.
  const runBulk = async (action, targets, reason) => {
    const verb = action === 'withdraw' ? 'withdrawn' : 'deleted';
    setBulkBusy(true);
    let failed = 0;
    let firstError = '';
    for (const candidate of targets) {
      try {
        if (action === 'withdraw') await withdrawCandidate(candidate.id, reason);
        else await deleteCandidate(candidate.id);
      } catch (err) {
        failed += 1;
        firstError = firstError || `${candidate.reference_no}: ${err.response?.data?.message || 'failed'}`;
      }
    }
    setBulkBusy(false);
    setBulkAction(null);
    selection.clear();
    queryClient.invalidateQueries({ queryKey: ['candidates'] });
    queryClient.invalidateQueries({ queryKey: ['candidates-count'] });
    const ok = targets.length - failed;
    pushToast(
      failed === 0
        ? { type: 'success', message: `${ok} candidate${ok === 1 ? '' : 's'} ${verb}.` }
        : { type: 'error', message: `${ok} ${verb}, ${failed} failed — ${firstError}` }
    );
  };

  const columns = [
    {
      ...selectColumn(selection, (r) => r.id, isSelectable),
      label: (
        <input
          type="checkbox"
          checked={selection.allSelected}
          onChange={selection.toggleAll}
          disabled={selection.selectableCount === 0}
          aria-label="Select all candidates on this page"
          className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
        />
      ),
    },
    {
      key: 'photo',
      label: '',
      render: (row) => (
        <ProfilePhoto kind="candidates" id={row.id} hasPhoto={row.has_profile_photo} name={row.full_name} size="h-12 w-10" />
      ),
    },
    {
      key: 'reference_no',
      label: 'Reference No',
      render: (row) => (
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <Link to={`/candidates/${row.id}`} className="font-medium text-primary hover:underline">
              {row.reference_no}
            </Link>
            {row.needs_conversion_review && (
              <span title="Missing documents/details for a step carried over by a vehicle-type conversion">
                <Badge tone="amber">Missing docs</Badge>
              </span>
            )}
          </div>
          <CategoryBadge category={row.category} />
        </div>
      ),
    },
    {
      key: 'full_name',
      label: 'Full Name',
      render: (row) => (
        <div>
          <div>{row.full_name}</div>
          {row.nationality && <div className="text-xs text-gray-400">{row.nationality}</div>}
        </div>
      ),
    },
    {
      key: 'current_step',
      label: 'Current Step',
      render: (row) =>
        row.current_step ? (
          <div className="w-40">
            <StepProgress
              current={row.current_step.sort_order}
              total={row.current_step.total_steps}
              label={row.current_step.title}
              percent={row.current_step.progress_percent}
            />
          </div>
        ) : (
          <span className="text-gray-400">—</span>
        ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => {
        let sla;
        if (row.is_overdue) sla = <Badge tone="red">Overdue {row.days_overdue}d</Badge>;
        else if (row.sla_days) {
          const daysLeft = row.sla_days - Math.floor((Date.now() - new Date(row.created_at).getTime()) / 86400000);
          sla = <span className="text-xs text-gray-500">{daysLeft}d left</span>;
        }
        return (
          <div className="space-y-1">
            <StatusBadge status={row.status} />
            {sla && <div>{sla}</div>}
          </div>
        );
      },
    },
    { key: 'created_at', label: 'Registered', render: (row) => new Date(row.created_at).toLocaleDateString() },
    {
      key: 'actions',
      label: '',
      render: (row) => (
        <div className="flex items-center gap-1">
          <IconButton as={Link} to={`/candidates/${row.id}`} label="View" tone="primary">
            <IconEye className="h-4 w-4" />
          </IconButton>
          <Can perm="candidates.withdraw">
            {['registered', 'in_progress'].includes(row.status) && (
              <IconButton label="Backout" onClick={() => setWithdrawTarget(row)}>
                <IconWithdraw className="h-4 w-4" />
              </IconButton>
            )}
          </Can>
          <Can perm="candidates.convert-vehicle-type">
            {['registered', 'in_progress'].includes(row.status) &&
              targetVehicleCategory(row.category, categoriesQuery.data) && (
                <IconButton
                  label={`Convert to ${row.category.vehicle_type === 'taxi' ? 'Bike' : 'Taxi'}`}
                  onClick={() => setConvertTarget(row)}
                >
                  <IconSwap className="h-4 w-4" />
                </IconButton>
              )}
          </Can>
          <Can perm="candidates.delete">
            <IconButton label="Delete" tone="danger" onClick={() => setDeleteTarget(row)}>
              <IconTrash className="h-4 w-4" />
            </IconButton>
          </Can>
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Candidates"
      />

      <div className="mb-4 flex gap-1 border-b border-gray-200">
        {STATUS_TABS.map((tab, i) => (
          <button
            key={tab.value}
            onClick={() => {
              setActiveTab(tab.value);
              setPage(1);
            }}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
              activeTab === tab.value
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
            {countQueries[i]?.data !== undefined && (
              <span className="ml-1.5 text-xs text-gray-400">({countQueries[i].data})</span>
            )}
          </button>
        ))}
      </div>

      <CandidateFilterBar {...filters.barProps}>
        <Input
          placeholder="Search name, reference, passport, contact…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-72"
        />
        <DatePicker value={createdFrom} onChange={(e) => { setCreatedFrom(e.target.value); setPage(1); }} className="w-40" />
        <DatePicker value={createdTo} onChange={(e) => { setCreatedTo(e.target.value); setPage(1); }} className="w-40" />
      </CandidateFilterBar>

      {selection.selectedIds.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-3 rounded-md border border-gray-200 bg-gray-50 px-4 py-2 text-sm">
          <span className="font-medium text-gray-700">{selection.selectedIds.length} selected</span>
          <Button variant="secondary" onClick={() => setBulkAction('withdraw')} disabled={withdrawable.length === 0}>
            Backout ({withdrawable.length})
          </Button>
          <Button variant="danger" onClick={() => setBulkAction('delete')} disabled={deletable.length === 0}>
            Delete ({deletable.length})
          </Button>
          <button onClick={selection.clear} className="ml-auto text-gray-500 hover:underline">
            Clear
          </button>
        </div>
      )}

      <SearchSummary search={search} total={candidatesQuery.data?.total} rows={rows} paged />

      <Table
        columns={withMatchColumn(columns, search)}
        data={rows}
        isLoading={candidatesQuery.isLoading}
        pagination={candidatesQuery.data}
        onPageChange={setPage}
        emptyMessage="No candidates found."
      />

      <WithdrawDialog
        open={bulkAction === 'withdraw'}
        onClose={() => !bulkBusy && setBulkAction(null)}
        onConfirm={(reason) => runBulk('withdraw', withdrawable, reason)}
        loading={bulkBusy}
      />

      <ConfirmDialog
        open={bulkAction === 'delete'}
        onClose={() => !bulkBusy && setBulkAction(null)}
        onConfirm={() => runBulk('delete', deletable)}
        title="Delete selected candidates"
        message={`Delete ${deletable.length} candidate(s)? This cannot be undone.`}
        confirmLabel="Delete"
        loading={bulkBusy}
      />

      <WithdrawDialog
        open={!!withdrawTarget}
        onClose={() => setWithdrawTarget(null)}
        onConfirm={(reason) => withdrawMutation.mutate({ id: withdrawTarget.id, reason })}
        loading={withdrawMutation.isPending}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
        title="Delete candidate"
        message={`Delete ${deleteTarget?.full_name} (${deleteTarget?.reference_no})? This cannot be undone.`}
        confirmLabel="Delete"
        loading={deleteMutation.isPending}
      />

      <ConvertVehicleTypeModal
        open={!!convertTarget}
        onClose={() => setConvertTarget(null)}
        candidate={convertTarget}
        categories={categoriesQuery.data}
      />
    </div>
  );
}
